import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  formatEatDateTime,
  formatMoney,
  moneyFromCents,
  parseIsoDay,
  formatDay,
} from '@wodi/domain';
import { DISCHARGE_DISPOSITIONS, type AdmissionView, type LocationView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
} from '@wodi/ui';
import { useState } from 'react';

import {
  admissionQuery,
  cancelAdmission,
  dischargePatient,
  movePatient,
} from '../api/inpatient.ts';
import { stayBalanceQuery, stayBillingQuery } from '../api/inpatient-billing.ts';
import { can, sessionQuery } from '../api/session.ts';
import { money } from '../billing/format.ts';
import { StayBillingSection } from '../inpatient-billing/stay-billing.tsx';
import { visitTheatreCasesQuery } from '../api/theatre.ts';
import { useOnline } from '../hooks/use-online.ts';
import { DietSection } from '../kitchen/diet-section.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { toasts } from '../toasts.ts';
import { errorText } from './bed-board-page.tsx';
import styles from './inpatient.module.css';
import { NO_BED, PlacePicker, useBedNeedsCleaning } from './place-picker.tsx';

// One inpatient stay (docs/plans/phase2-inpatient-adt.md §6): where the patient is, how
// they got there, the bed days charged, and give a bed, move, discharge or cancel.

const route = getRouteApi('/_app/admissions/$admissionId');

type Disposition = (typeof DISCHARGE_DISPOSITIONS)[number];

/** TODO(clinical-verify) B9: the provisional disposition list, in words. */
const DISPOSITION_WORDS: Record<Disposition, string> = {
  home: 'Home',
  referred: 'Referred to another facility',
  against_medical_advice: 'Left against medical advice',
  absconded: 'Absconded',
  died: 'Died',
};

const STATUS_TAGS: Record<AdmissionView['status'], [string, 'active' | 'done' | 'neutral']> = {
  admitted: ['Admitted', 'active'],
  discharged: ['Discharged', 'done'],
  cancelled: ['Cancelled', 'neutral'],
};

function place(l: LocationView): string {
  return l.bed === null
    ? `${l.ward.name}, waiting for a bed`
    : `${l.ward.name}, bed ${l.bed.label}`;
}

function dayText(iso: string): string {
  const day = parseIsoDay(iso);
  return day.ok ? formatDay(day.value) : iso;
}

const END_WORDS: Record<NonNullable<LocationView['endReason']>, string> = {
  moved: 'Moved',
  discharged: 'Discharged',
  cancelled: 'Admission cancelled',
};

function MoveDialog({ stay, onClose }: { stay: AdmissionView; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const current = stay.current;
  const [wardId, setWardId] = useState<string | null>(current?.ward.id ?? null);
  const [bedId, setBedId] = useState<string>(NO_BED);
  const [reason, setReason] = useState('');
  const [cleaningReason, setCleaningReason] = useState('');
  const needsCleaning = useBedNeedsCleaning(wardId, bedId);
  const firstBed = current?.bed === null && wardId === current.ward.id;
  const move = useMutation({
    mutationFn: () =>
      movePatient(stay.id, {
        wardId: wardId ?? '',
        bedId: bedId === NO_BED ? null : bedId,
        ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
        ...(needsCleaning ? { cleaningOverrideReason: cleaningReason.trim() } : {}),
      }),
    onSuccess: async (next) => {
      await queryClient.invalidateQueries({ queryKey: ['inpatient'] });
      toasts.show({
        title: firstBed ? 'Bed given' : 'Patient moved',
        description: next.current === null ? '' : place(next.current),
      });
      onClose();
    },
  });
  const ready =
    online &&
    wardId !== null &&
    (firstBed ? bedId !== NO_BED : reason.trim().length >= 5) &&
    (!needsCleaning || cleaningReason.trim().length >= 5);
  return (
    <Dialog
      title={current?.bed === null ? 'Give a bed' : 'Move patient'}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <PlacePicker
        wardId={wardId}
        bedId={bedId}
        exceptBedId={current?.bed?.id ?? null}
        onWard={(id) => {
          setWardId(id);
          setBedId(NO_BED);
        }}
        onBed={setBedId}
        cleaningReason={cleaningReason}
        onCleaningReason={setCleaningReason}
      />
      {firstBed ? null : (
        <TextArea
          label="Why is the patient moving?"
          description="At least 5 characters. Kept with the stay's history."
          value={reason}
          onChange={setReason}
        />
      )}
      {move.error === null ? null : (
        <Notice tone="error">
          {errorText(move.error, "The patient wasn't moved. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={move.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            move.mutate();
          }}
        >
          {firstBed ? 'Give bed' : 'Move patient'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DischargeDialog({ stay, onClose }: { stay: AdmissionView; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [disposition, setDisposition] = useState<Disposition | null>(null);
  const [condition, setCondition] = useState('');
  const [summary, setSummary] = useState('');
  const [instructions, setInstructions] = useState('');
  const discharge = useMutation({
    mutationFn: () =>
      dischargePatient(stay.id, {
        disposition: disposition ?? 'home',
        condition: condition.trim(),
        summary: summary.trim(),
        ...(instructions.trim() === '' ? {} : { instructions: instructions.trim() }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['inpatient'] });
      await queryClient.invalidateQueries({ queryKey: ['stay-billing'] });
      toasts.show({ title: 'Patient discharged', description: 'The bed is waiting to be cleaned' });
      onClose();
    },
  });
  const ready = online && disposition !== null && condition.trim() !== '' && summary.trim() !== '';
  return (
    <Dialog
      title="Discharge patient"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Select
        label="Discharged to"
        placeholder="Choose one"
        options={DISCHARGE_DISPOSITIONS.map((d) => ({ id: d, label: DISPOSITION_WORDS[d] }))}
        value={disposition}
        onChange={(v) => {
          setDisposition(v as Disposition);
        }}
      />
      <TextArea label="Condition at discharge" value={condition} onChange={setCondition} rows={2} />
      <TextArea label="Discharge summary" value={summary} onChange={setSummary} rows={4} />
      <TextArea
        label="Follow-up instructions (optional)"
        value={instructions}
        onChange={setInstructions}
        rows={2}
      />
      <p className={styles.meta}>
        Bed days still owed are charged now, the bed goes for cleaning and the stay's visit closes.
      </p>
      <DischargeBalance admissionId={stay.id} />
      {discharge.error === null ? null : (
        <Notice tone="error">
          {errorText(discharge.error, "The patient wasn't discharged. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={discharge.isPending}
          pendingLabel="Discharging…"
          onPress={() => {
            discharge.mutate();
          }}
        >
          Discharge patient
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The bill's balance in the discharge dialog (inpatient billing plan §1.1 item 7): a warning,
 * never a block. Clinical discharge never waits for payment; the balance is recorded.
 */
function DischargeBalance({ admissionId }: { admissionId: string }) {
  const balance = useQuery(stayBalanceQuery(admissionId));
  if (balance.isPending) return <p className={styles.meta}>Checking the bill…</p>;
  if (balance.isError)
    return (
      <Notice tone="info">
        The bill couldn't be checked. You can still discharge; the cashier will see the balance.
      </Notice>
    );
  const b = balance.data;
  if (b.balanceState === 'owes')
    return (
      <Notice tone="info">
        {`The bill is not settled: the patient owes ${money(b.amountOwed)} after deposits. You can still discharge. The balance is recorded with your name, and the cashier follows it up.`}
      </Notice>
    );
  if (b.balanceState === 'refund_due')
    return (
      <Notice tone="info">
        {`The deposit is more than the bill so far: ${money(b.refundDue)} is due back to the patient after the final bill.`}
      </Notice>
    );
  return <p className={styles.meta}>The bill is settled.</p>;
}

/** The stay's bill, for someone who may read bills (billing.read). */
function StayBill({ admissionId }: { admissionId: string }) {
  const billing = useQuery(stayBillingQuery(admissionId));
  if (billing.isPending) return <LoadingBlock label="Loading the bill" rows={3} />;
  if (billing.isError)
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void billing.refetch()}>Try again</Button>}
      >
        {errorText(billing.error, "The stay's bill couldn't be loaded. Try again.")}
      </Notice>
    );
  return <StayBillingSection billing={billing.data} place="stay" />;
}

function CancelDialog({ stay, onClose }: { stay: AdmissionView; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const cancel = useMutation({
    mutationFn: () => cancelAdmission(stay.id, reason.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['inpatient'] });
      toasts.show({ title: 'Admission cancelled', description: 'Entered in error' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Cancel admission"
      tone="alert"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>
        Only for an admission entered in error. Bed days not yet on a bill are cancelled with it.
      </p>
      <TextArea label="Why was it entered in error?" value={reason} onChange={setReason} />
      {cancel.error === null ? null : (
        <Notice tone="error">
          {errorText(cancel.error, "The admission wasn't cancelled. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep admission
        </Button>
        <Button
          variant="destructive"
          isDisabled={!online || reason.trim().length < 5}
          isPending={cancel.isPending}
          pendingLabel="Cancelling…"
          onPress={() => {
            cancel.mutate();
          }}
        >
          Cancel admission
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The stay's theatre cases (docs/plans/phase2-theatre.md §6), and booking one. */
function StayTheatre({ stay, mayBook }: { stay: AdmissionView; mayBook: boolean }) {
  const cases = useQuery(visitTheatreCasesQuery(stay.visit.id));
  return (
    <Section
      title="Theatre"
      actions={
        mayBook && stay.status === 'admitted' ? (
          <Link to="/visits/$visitId/theatre-booking" params={{ visitId: stay.visit.id }}>
            Book theatre
          </Link>
        ) : undefined
      }
    >
      {cases.isError ? (
        <Notice tone="error">{errorText(cases.error, "Theatre cases couldn't be loaded.")}</Notice>
      ) : cases.isPending ? (
        <LoadingBlock label="Loading theatre cases" rows={1} />
      ) : cases.data.length === 0 ? (
        <p className={styles.meta}>No theatre cases for this stay.</p>
      ) : (
        <ul className={styles.waiting} aria-label="Theatre cases">
          {cases.data.map((c) => (
            <li key={c.id} className={styles.waitingRow}>
              <Link to="/theatre/cases/$caseId" params={{ caseId: c.id }}>
                {`${c.procedureName}, ${c.theatreName}, ${formatEatDateTime(new Date(c.scheduledStart))}`}
              </Link>
              <span className={styles.meta}>{THEATRE_STATUS_WORDS[c.status]}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

const THEATRE_STATUS_WORDS = {
  booked: 'Booked',
  in_theatre: 'In theatre',
  completed: 'Completed',
  cancelled: 'Cancelled',
} as const;

export function AdmissionPage() {
  const { admissionId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const stay = useQuery(admissionQuery(admissionId));
  const online = useOnline();
  const [open, setOpen] = useState<'move' | 'discharge' | 'cancel' | null>(null);

  if (stay.isPending) return <LoadingBlock label="Loading the stay" rows={6} />;
  if (stay.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void stay.refetch()}>Try again</Button>}>
        {errorText(stay.error, "The stay couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const s = stay.data;
  const admitted = s.status === 'admitted';
  const allowed = (p: Parameters<typeof can>[1]) => session !== null && can(session, p);
  const [statusWord, statusShape] = STATUS_TAGS[s.status];
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={s.patientId} />
      <PageHeader
        title="Inpatient stay"
        description={
          s.current === null
            ? `Admitted ${formatEatDateTime(new Date(s.admittedAt))}`
            : `${place(s.current)}. Day ${String(s.daysAdmitted)}.`
        }
        actions={
          admitted ? (
            <>
              {allowed('inpatient.move') ? (
                <Button
                  variant={s.current?.bed === null ? 'primary' : 'secondary'}
                  isDisabled={!online}
                  onPress={() => {
                    setOpen('move');
                  }}
                >
                  {s.current?.bed === null ? 'Give a bed' : 'Move patient'}
                </Button>
              ) : null}
              {allowed('inpatient.discharge') ? (
                <Button
                  variant={s.current?.bed === null ? 'secondary' : 'primary'}
                  isDisabled={!online}
                  onPress={() => {
                    setOpen('discharge');
                  }}
                >
                  Discharge patient
                </Button>
              ) : null}
              {allowed('inpatient.admission.cancel') ? (
                <Button
                  variant="quiet"
                  isDisabled={!online}
                  onPress={() => {
                    setOpen('cancel');
                  }}
                >
                  Cancel admission
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. Moving and discharging wait for the connection.
        </Notice>
      ) : null}
      {allowed('clinical.read') ? (
        <nav className={styles.actions} aria-label="Clinical care in this stay">
          <Link to="/admissions/$admissionId/ward-round" params={{ admissionId: s.id }}>
            Ward round
          </Link>
          <Link to="/admissions/$admissionId/nursing" params={{ admissionId: s.id }}>
            Nursing
          </Link>
          <Link to="/admissions/$admissionId/medicines" params={{ admissionId: s.id }}>
            Medicines
          </Link>
          {allowed('maternity.read') ? (
            <Link to="/patients/$patientId/maternity" params={{ patientId: s.patientId }}>
              Maternity
            </Link>
          ) : null}
        </nav>
      ) : null}
      {admitted && (allowed('deaths.record') || allowed('births.record')) ? (
        <nav className={styles.actions} aria-label="Death and birth">
          {allowed('deaths.record') ? (
            <Link to="/visits/$visitId/record-death" params={{ visitId: s.visit.id }}>
              Record death
            </Link>
          ) : null}
          {allowed('births.record') ? (
            <Link to="/visits/$visitId/record-birth" params={{ visitId: s.visit.id }}>
              Record birth
            </Link>
          ) : null}
        </nav>
      ) : null}
      <Section title="Admission" actions={<Tag state={statusShape}>{statusWord}</Tag>}>
        <dl className={styles.facts}>
          <dt>Stay visit</dt>
          <dd className="wodi-id">{s.visit.visitNumber}</dd>
          <dt>Admitted from</dt>
          <dd className="wodi-id">{s.sourceVisit.visitNumber}</dd>
          <dt>Admitted</dt>
          <dd>{`${formatEatDateTime(new Date(s.admittedAt))} by ${s.admittingClinician.displayName}`}</dd>
          <dt>Reason</dt>
          <dd className={styles.prose}>{s.reason}</dd>
          {s.cancelReason === null ? null : (
            <>
              <dt>Cancelled because</dt>
              <dd>{s.cancelReason}</dd>
            </>
          )}
        </dl>
      </Section>
      <DietSection admissionId={s.id} mayOrder={allowed('diet.order')} />
      {s.discharge === null ? null : (
        <Section title="Discharge summary">
          <dl className={styles.facts}>
            <dt>Discharged</dt>
            <dd>{`${formatEatDateTime(new Date(s.discharge.dischargedAt))} by ${s.discharge.dischargedBy.displayName}`}</dd>
            <dt>Discharged to</dt>
            <dd>{DISPOSITION_WORDS[s.discharge.disposition]}</dd>
            <dt>Condition</dt>
            <dd className={styles.prose}>{s.discharge.condition}</dd>
            <dt>Summary</dt>
            <dd className={styles.prose}>{s.discharge.summary}</dd>
            <dt>Follow-up</dt>
            <dd className={styles.prose}>{s.discharge.instructions ?? 'None given'}</dd>
          </dl>
        </Section>
      )}
      {allowed('billing.read') ? <StayBill admissionId={s.id} /> : null}
      <Section title="Where the patient has been">
        <Table flat dense aria-label="Locations">
          <TableHeader>
            <Column isRowHeader>Place</Column>
            <Column>From</Column>
            <Column>Until</Column>
            <Column>Why moved here</Column>
          </TableHeader>
          <TableBody items={s.history}>
            {(l) => (
              <Row id={l.id}>
                <Cell>{place(l)}</Cell>
                <Cell>{formatEatDateTime(new Date(l.startedAt))}</Cell>
                <Cell>
                  {l.endedAt === null
                    ? 'Now'
                    : `${formatEatDateTime(new Date(l.endedAt))}, ${END_WORDS[l.endReason ?? 'moved']}`}
                </Cell>
                <Cell>{l.reason ?? ''}</Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </Section>
      <Section
        title="Bed days"
        description="Counted at each midnight in the ward the patient was in; a stay with no midnight is one day (provisional rule)."
      >
        <Table flat dense aria-label="Bed days">
          <TableHeader>
            <Column isRowHeader>Day</Column>
            <Column>Ward</Column>
            <Column>Charge</Column>
            <Column>Amount</Column>
          </TableHeader>
          <TableBody
            items={s.bedDays}
            renderEmptyState={() =>
              'No bed days charged yet. They are charged after each midnight and at discharge.'
            }
          >
            {(d) => (
              <Row id={d.id}>
                <Cell>{dayText(d.serviceDate)}</Cell>
                <Cell>{d.wardName}</Cell>
                <Cell>
                  {d.chargeStatus === null
                    ? 'Not charged'
                    : d.chargeStatus === 'pending'
                      ? 'Waiting to be billed'
                      : d.chargeStatus === 'billed'
                        ? 'On a bill'
                        : 'Cancelled'}
                </Cell>
                <Cell>
                  <span className={styles.money}>
                    {d.total === null
                      ? 'Not priced'
                      : formatMoney(moneyFromCents(BigInt(d.total.cents)))}
                  </span>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </Section>
      {allowed('theatre.read') ? <StayTheatre stay={s} mayBook={allowed('theatre.book')} /> : null}
      <p className={styles.meta}>
        <Link to="/wards">Back to the wards</Link>
      </p>
      {open === 'move' ? (
        <MoveDialog
          stay={s}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open === 'discharge' ? (
        <DischargeDialog
          stay={s}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open === 'cancel' ? (
        <CancelDialog
          stay={s}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
    </div>
  );
}
